import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon, LogoutIcon, TimesIcon } from '../../../styled/Icon';
import RouterLink from '../../../Router/Link';

import {
  MobileMenuWrapper,
  CloseButton,
  BackButton,
  BackButtonContent,
  MenuHead,
  MenuBody,
  LinkList,
  Link,
  LinkLabel,
  LinkIcon,
  UiBlock,
} from '../../../styled/Header/Nav/wrapper';
import { useMember } from '../../../providers/member';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import Distance from '../../../styled/Distance';
import {
  useFireTrackingEvent,
  useTrackGenericEvent,
} from '../../../hooks/tracking/useTrackingEvents';

interface ILinkItem {
  item: IMenuItem;
  onSelect: (item: IMenuItem) => void;
  onClose: () => void;
  index: number;
}

const LinkItem = ({ onSelect, onClose, item, index }: ILinkItem) => {
  const { children, url, name } = item;
  const trackingEvent = useMemo(() => ({ event: 'menu_click', value: item.name }), [item.name]);
  const track = useTrackGenericEvent(trackingEvent);

  const trackAndClose = useCallback(() => {
    track();
    onClose();
  }, [onClose, track]);

  const click = useCallback(() => {
    track();
    onSelect(item);
  }, [item, onSelect, track]);

  if (children.length) {
    return (
      <RouterLink
        Component={Link}
        onClick={click}
        title={name}
        href={url}
        className={`link-${index++}`}>
        <LinkLabel>{name}</LinkLabel>
        <LinkIcon>
          <ChevronRightIcon strokeWidth="2" />
        </LinkIcon>
      </RouterLink>
    );
  }

  return (
    <RouterLink
      className={`link-${index++}`}
      Component={Link}
      href={url}
      onPostNavigate={trackAndClose}
      title={name}>
      {name}
    </RouterLink>
  );
};

interface INavigation {
  onClose: () => void;
  items: IMenuItem[];
  initialItem?: IMenuItem;
}

/**
 * Menu config
 */
// Defines how low and high the menu head can go (also needs change in css)
const headHeightSpan = [60, 120];
// Defines how quick header compresses when scrolling
const floatRatio = 0.7;

const Navigation = ({ initialItem, items, onClose }: INavigation) => {
  const { authorized } = useMember();
  const [stack, setStack] = useState<IMenuItem[]>((initialItem && [initialItem]) || []);
  const [headHeight, setHeadHeight] = useState<number>(120);
  const menuBody = useRef<HTMLDivElement | null>(null);
  let linkCount = 1;
  const track = useFireTrackingEvent();

  const trackAndClose = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      track({ event: 'menu_click', value: (e.target as HTMLElement).title });
      onClose();
    },
    [onClose],
  );

  const onBodyScroll = (): void => {
    const scrollTop = (menuBody.current?.scrollTop || 0) * floatRatio;
    setHeadHeight(Math.max(headHeightSpan[0], headHeightSpan[1] - scrollTop));
  };

  useEffect(() => {
    if (menuBody.current) {
      disableBodyScroll(menuBody.current);
      menuBody.current.addEventListener('scroll', onBodyScroll);
    }

    return (): void => {
      clearAllBodyScrollLocks();
      if (menuBody.current) {
        menuBody.current.removeEventListener('scroll', onBodyScroll);
      }
    };
  }, []);

  const selectItem = useCallback((item: IMenuItem) => {
    setStack((prev) => [item].concat(prev));
  }, []);

  const onBack = useCallback(() => {
    track({ event: 'menu_back_click' });
    setStack((prev) => prev.slice(1));
  }, []);

  const primary = !stack.length && items?.filter((i) => i.highlighted && !i.hiddenFromMenu);
  const secondary = !stack.length && items?.filter((i) => !i.highlighted && !i.hiddenFromMenu);

  return (
    <>
      <MobileMenuWrapper>
        <MenuHead
          className={stack.length ? '' : 'no-back-button'}
          style={{ height: `${headHeight}px` }}>
          {!!stack.length && (
            <BackButton onClick={onBack}>
              <BackButtonContent>
                <ChevronLeftIcon strokeWidth="2" />
              </BackButtonContent>
              <BackButtonContent>Tillbaka</BackButtonContent>
            </BackButton>
          )}
          <CloseButton onClick={onClose} title="Stäng navigering">
            <TimesIcon />
          </CloseButton>
        </MenuHead>
        <MenuBody ref={menuBody}>
          {primary && (
            <LinkList className="main">
              {primary.map((item) => (
                <LinkItem
                  key={`${item.url}__${item.name}`}
                  item={item}
                  onSelect={selectItem}
                  onClose={onClose}
                  index={linkCount++}
                />
              ))}
            </LinkList>
          )}
          {secondary && (
            <LinkList className="secondary">
              {secondary.map((item) => (
                <LinkItem
                  key={`${item.url}__${item.name}`}
                  item={item}
                  onSelect={selectItem}
                  onClose={onClose}
                  index={linkCount++}
                />
              ))}
            </LinkList>
          )}
          {!stack.length && (
            <>
              <Distance size="4" />
              <LinkList className="secondary">
                <RouterLink
                  data-reload
                  Component={Link}
                  title="Mina sidor"
                  href="/mina-sidor"
                  onBeforeNavigate={trackAndClose}
                  className={`link-${linkCount++}`}>
                  Mina sidor
                </RouterLink>
                <RouterLink
                  Component={Link}
                  title="Kundservice"
                  href="/kundservice"
                  onBeforeNavigate={trackAndClose}
                  className={`link-${linkCount++}`}>
                  Kundservice
                </RouterLink>
                {authorized && (
                  <RouterLink
                    Component={Link}
                    title="Logga ut"
                    data-reload
                    href="/auth/logout"
                    onBeforeNavigate={trackAndClose}
                    className={`link-${linkCount++} ghost`}>
                    <div className="l-flex flex-a-c">
                      <LogoutIcon className="inline" />
                      <span className="bump-left">Logga ut</span>
                    </div>
                  </RouterLink>
                )}
              </LinkList>
            </>
          )}
          {stack?.[0]?.childNodeLabel && stack?.[0]?.useSelfAsChild && (
            <LinkList className="heading-list">
              <RouterLink
                Component={Link}
                href={stack[0].url}
                onBeforeNavigate={trackAndClose}
                title={stack?.[0]?.childNodeLabel}
                className={`link-${linkCount++}`}>
                {stack?.[0]?.childNodeLabel}
              </RouterLink>
            </LinkList>
          )}
          {!!stack.length && (
            <LinkList>
              {stack[0].children.map(
                (item) =>
                  !item.hiddenFromMenu && (
                    <LinkItem
                      key={`${item.url}__${item.name}`}
                      item={item}
                      onSelect={selectItem}
                      onClose={onClose}
                      index={linkCount++}
                    />
                  ),
              )}
            </LinkList>
          )}
        </MenuBody>
      </MobileMenuWrapper>
      <UiBlock onClick={onClose} title="Stäng navigering" />
    </>
  );
};

export default Navigation;
